import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase, testConnection } from "@/integrations/supabase/client";
import { getCurrentUser, getProfile, awardDailyLoginExperience, awardNewUserBadge } from '@/lib/supabase';
import { repairUserBadges, checkAndRepairUserBadges } from '@/lib/repairAllUserBadges';
import type { User } from '@supabase/supabase-js';
import { toast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  profile: any | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, profileData?: ProfileData) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isGoogleAccount: (email: string) => Promise<boolean>;
  setPasswordForGoogleAccount: (password: string) => Promise<boolean>;
}

interface ProfileData {
  username?: string;
  display_name?: string;
  bio?: string | null;
  avatar_url?: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      setLoading(true);
      try {
        console.log('Initializing authentication...');
        
        // Create a timeout for the entire auth initialization process
        const initTimeout = setTimeout(() => {
          console.error('Auth initialization timed out');
          setConnectionError('Authentication initialization timed out');
          setLoading(false);
        }, 10000); // 10 second timeout for entire process
        
        // First test the connection to Supabase
        console.log('Testing Supabase connection...');
        const connectionTest = await testConnection();
        
        if (!connectionTest.connected) {
          console.error('Failed to connect to Supabase:', connectionTest.error);
          setConnectionError(connectionTest.error);
          clearTimeout(initTimeout);
          setLoading(false);
          return;
        }
        
        // Try to get session with a timeout
        console.log('Getting auth session...');
        try {
          const sessionPromise = supabase.auth.getSession();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Session fetch timed out')), 6000)
          );
          
          const { data: { session } } = await Promise.race([
            sessionPromise,
            timeoutPromise.then(() => {
              throw new Error('Session fetch timed out');
            })
          ]) as any;
          
          if (session?.user) {
            console.log('User found in session:', session.user.id);
            setUser(session.user);
            
            // Get profile with timeout
            try {
              console.log('Fetching user profile...');
              const profile = await getProfile(session.user.id);
              setProfile(profile);
            } catch (profileError) {
              console.error('Error fetching profile:', profileError);
              // Continue even if profile fetch fails
            }
          } else {
            console.log('No user in session');
          }
        } catch (sessionError) {
          console.error('Error getting session:', sessionError);
          setConnectionError(sessionError instanceof Error ? sessionError.message : 'Failed to get auth session');
        }
        
        clearTimeout(initTimeout);
      } catch (error) {
        console.error('Error loading auth:', error);
        setConnectionError(error instanceof Error ? error.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id);
        
        if (session?.user) {
          setUser(session.user);
          
          if (event === 'SIGNED_IN') {
            // For Google sign-ins, update the profile with Google data if it exists
            const googleData = session.user.user_metadata;
            if (googleData) {
              console.log('Google auth data detected:', googleData);
              
              // Check if this user already has a profile
              const existingProfile = await getProfile(session.user.id);
              
              // If we have Google data with avatar and name, update the profile
              if (googleData.avatar_url || googleData.full_name) {
                const updateData: any = {};
                
                // Only update these fields if they come from Google
                if (googleData.avatar_url) {
                  updateData.avatar_url = googleData.avatar_url;
                }
                
                if (googleData.full_name) {
                  updateData.display_name = googleData.full_name;
                }
                
                // If no existing username, create one from email
                if (!existingProfile?.username) {
                  updateData.username = session.user.email?.split('@')[0].toLowerCase() || `user_${Date.now()}`;
                }
                
                // Update profile with Google data
                console.log('Updating profile with Google data:', updateData);
                
                try {
                  const { error } = await supabase
                    .from('profiles')
                    .update(updateData)
                    .eq('id', session.user.id);
                    
                  if (error) {
                    console.error('Error updating profile with Google data:', error);
                  } else {
                    console.log('Profile updated with Google data successfully');
                  }
                } catch (err) {
                  console.error('Exception updating profile:', err);
                }
              }
            }
            
            // Get the updated profile
            const profile = await getProfile(session.user.id);
            setProfile(profile);
            
            // Call our consolidated post-login function
            await handleUserSignedIn(session.user.id);
          }
        } else {
          setUser(null);
          setProfile(null);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const refreshProfile = async () => {
    if (user) {
      const profile = await getProfile(user.id);
      setProfile(profile);
      
      // Also check and repair badges silently in the background
      try {
        console.log(`Checking and repairing badges during profile refresh for user ${user.id}`);
        const badgeResult = await checkAndRepairUserBadges(user.id);
        
        // Show toast notifications for newly earned badges
        if (badgeResult.success && badgeResult.newlyEarnedBadges.length > 0) {
          // If there's just one badge, show a detailed notification
          if (badgeResult.newlyEarnedBadges.length === 1) {
            const badge = badgeResult.newlyEarnedBadges[0];
            toast({
              title: `${badge.icon} New Badge Earned: ${badge.name}`,
              description: badge.description,
              variant: "default"
            });
          } 
          // If there are multiple badges, show a summary
          else {
            toast({
              title: "🎉 New Badges Earned!",
              description: `You've earned ${badgeResult.newlyEarnedBadges.length} new badges! Check your profile to see them.`,
              variant: "default"
            });
          }
        }
      } catch (badgeError) {
        // Don't show toast to user, just log the error
        console.error('Error repairing badges during profile refresh:', badgeError);
      }
    }
  };

  async function handleUserSignedIn(userId: string) {
    console.log(`User ${userId} signed in, running post-login tasks...`);
    
    // Award daily login experience
    try {
      const expResult = await awardDailyLoginExperience(userId);
      if (expResult.leveledUp) {
        console.log(`User ${userId} leveled up to ${expResult.level} from daily login!`);
      }
    } catch (expError) {
      console.error('Error awarding daily login experience:', expError);
    }
    
    // Check and repair badges silently in the background
    try {
      console.log(`Checking and repairing badges for user ${userId}`);
      const badgeResult = await checkAndRepairUserBadges(userId);
      
      // Show toast notifications for newly earned badges
      if (badgeResult.success && badgeResult.newlyEarnedBadges.length > 0) {
        // If there's just one badge, show a detailed notification
        if (badgeResult.newlyEarnedBadges.length === 1) {
          const badge = badgeResult.newlyEarnedBadges[0];
          toast({
            title: `${badge.icon} New Badge Earned: ${badge.name}`,
            description: badge.description,
            variant: "default"
          });
        } 
        // If there are multiple badges, show a summary
        else {
          toast({
            title: "🎉 New Badges Earned!",
            description: `You've earned ${badgeResult.newlyEarnedBadges.length} new badges! Check your profile to see them.`,
            variant: "default"
          });
        }
      }
    } catch (badgeError) {
      // Don't show toast to user, just log the error
      console.error('Error repairing badges on login:', badgeError);
    }
  }

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      if (data.user) {
        setUser(data.user);
        const profile = await getProfile(data.user.id);
        setProfile(profile);
        
        // Call our consolidated post-login function
        await handleUserSignedIn(data.user.id);
      }
    } catch (error: any) {
      console.error('Error signing in:', error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      console.log('Initiating Google sign-in...');
      
      // Dynamically determine the appropriate redirect URL
      // This handles Vercel deployments properly
      let baseUrl = window.location.origin;
      const hostname = window.location.hostname;
      
      // For production, prefer using the custom domain if available
      if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
        // If we're on the custom domain, use it
        if (hostname === 'www.vibebops.com' || hostname === 'vibebops.com') {
          baseUrl = 'https://www.vibebops.com';
          console.log('Using custom domain URL:', baseUrl);
        } else {
          // We're on Vercel but not the custom domain - we're on the vercel.app domain
          baseUrl = `https://${hostname}`;
          console.log('Using Vercel URL:', baseUrl);
        }
      } else {
        console.log('Using development URL:', baseUrl);
      }
      
      // Always use the main login page as the redirect target
      // The OAuth callback is handled directly on this page
      const redirectUrl = `${baseUrl}/login`;
      
      console.log(`Setting redirect URL to: ${redirectUrl}`);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          scopes: 'email profile',
        }
      });
      
      if (error) {
        console.error('Google OAuth error:', error);
        throw error;
      }
      
      if (data?.url) {
        console.log('Redirecting to Google OAuth URL:', data.url);
        
        // For both production and development, just use the standard redirect
        window.location.href = data.url;
      }
    } catch (error: any) {
      console.error('Error signing in with Google:', error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, profileData?: ProfileData) => {
    setLoading(true);
    try {
      console.log('Starting signup process with:', { email, profileData });
      
      // Create userMetadata object with profile information
      const metadata = {
        username: profileData?.username || email.split('@')[0],
        name: profileData?.display_name || email.split('@')[0],
        bio: profileData?.bio || null,
        avatar_url: profileData?.avatar_url || null
      };
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });
      
      if (error) throw error;
      
      console.log('User signed up successfully:', data.user?.id);
      
      // Award the "New User" badge if user was created successfully
      if (data.user?.id) {
        try {
          // Small delay to ensure the profile is fully created in the database
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Award the New User badge
          const badgeResult = await awardNewUserBadge(data.user.id);
          if (badgeResult) {
            console.log(`New User badge awarded to user ${data.user.id}`);
          } else {
            console.warn(`Failed to award New User badge to user ${data.user.id}`);
          }
        } catch (badgeError) {
          console.error('Error awarding New User badge:', badgeError);
          // Continue with signup even if badge award fails
        }
      }
      
      // Profile will be created automatically through database trigger
    } catch (error: any) {
      console.error('Error signing up:', error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      // Trigger the pre-signout event for component cleanup
      console.log('Triggering pre-signout event');
      window.dispatchEvent(new CustomEvent('app:pre-signout'));
      
      // Small delay to ensure event handlers complete
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Clean up any active subscriptions before signing out
      try {
        // Unsubscribe from all channels using current Supabase API
        console.log('Unsubscribing from all Supabase channels before sign out');
        const allChannels = supabase.getChannels();
        for (const channel of allChannels) {
          await channel.unsubscribe();
        }
      } catch (cleanupError) {
        console.error('Error cleaning up subscriptions:', cleanupError);
        // Continue with sign out even if cleanup fails
      }

      // Small delay to ensure all subscriptions are cleaned up
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setProfile(null);
    } catch (error: any) {
      console.error('Error signing out:', error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Helper method to check if a user is a Google account WITHOUT password
  const isGoogleAccount = async (email: string): Promise<boolean> => {
    try {
      // Check if the user exists first without creating a new user
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
        }
      });

      // If we specifically get the "registered but has no password" error,
      // we know this is a Google-only account (no password set)
      if (error && 
          error.message && 
          (error.message.includes("is registered but has no password") || 
           error.message.includes("Email link invalid"))) {
        console.log(`User ${email} is a Google-only account (no password set)`, error);
        return true;
      }

      // If we don't get that specific error, the user either:
      // 1. Doesn't exist
      // 2. Has a password set (can be a Google account OR regular account)
      // In either case, we let them try to sign in with password
      return false;
    } catch (error) {
      console.error('Error checking if user is a Google account:', error);
      return false;
    }
  };

  /**
   * Allow a Google-authenticated user to set a password
   * This enables them to log in with either Google or email/password
   */
  const setPasswordForGoogleAccount = async (password: string): Promise<boolean> => {
    if (!user) {
      console.error('Cannot set password: No user is logged in');
      return false;
    }
    
    setLoading(true);
    try {
      // Update the user's password
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) {
        console.error('Error setting password for Google account:', error);
        return false;
      }
      
      console.log('Password set successfully for Google account');
      return true;
    } catch (error) {
      console.error('Exception setting password for Google account:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    refreshProfile,
    isGoogleAccount,
    setPasswordForGoogleAccount
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
